<!-- src/components/RangeSlider.vue -->
<template>
  <div class="w-full relative" ref="sliderRef">
    <div class="relative w-full h-2 bg-gray-300 rounded-full">
      <!-- Selected Range -->
      <div
        class="absolute h-2 bg-blue-500 "
        :style="rangeStyle"
      ></div>

      <!-- Left Handle (Start) -->
      <div
        class="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-blue-500 rounded-full cursor-pointer shadow-md touch-manipulation transition-transform duration-150 hover:scale-110 focus:scale-110"
        :style="{ left: start + '%' }"
        @mousedown="initDrag('start', $event)"
        @touchstart="initDrag('start', $event)"
        tabindex="0"
        @keydown.left.prevent="moveHandle('start', -1)"
        @keydown.right.prevent="moveHandle('start', 1)"
        aria-label="Minimum Days Handle"
        role="slider"
        :aria-valuemin="min"
        :aria-valuemax="endValue"
        :aria-valuenow="startValue"
        :aria-disabled="disabled"
        @focus="showTooltip.start = true"
        @blur="showTooltip.start = false"
      >
        <!-- Tooltip -->
        <div
          v-if="showTooltip.start && !disabled && isLargeScreen"
          class="absolute top-[-35px] left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap"
        >
          {{ startValue }} day<span v-if="startValue !== 1">s</span>
        </div>
      </div>

      <!-- Right Handle (End) -->
      <div
        class="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-6 h-6 bg-white border-2 border-blue-500 rounded-full cursor-pointer shadow-md touch-manipulation transition-transform duration-150 hover:scale-110 focus:scale-110"
        :style="{ left: end + '%' }"
        @mousedown="initDrag('end', $event)"
        @touchstart="initDrag('end', $event)"
        tabindex="0"
        @keydown.left.prevent="moveHandle('end', -1)"
        @keydown.right.prevent="moveHandle('end', 1)"
        aria-label="Maximum Days Handle"
        role="slider"
        :aria-valuemin="startValue"
        :aria-valuemax="max"
        :aria-valuenow="endValue"
        :aria-disabled="disabled"
        @focus="showTooltip.end = true"
        @blur="showTooltip.end = false"
      >
        <!-- Tooltip -->
        <div
          v-if="showTooltip.end && !disabled && isLargeScreen"
          class="absolute top-[-35px] left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap"
        >
          {{ endValue }} day<span v-if="endValue !== 1">s</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useScreenSize} from "@/composables/useScreenSize";

const { isLargeScreen } = useScreenSize();

// Define Props
const props = defineProps({
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 14,
  },
  startValue: {
    type: Number,
    default: 3,
  },
  endValue: {
    type: Number,
    default: 5,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
});

// Define Emits
const emit = defineEmits(['update:start', 'update:end']);

// References
const sliderRef = ref(null);
const start = ref(percentageFromValue(props.startValue));
const end = ref(percentageFromValue(props.endValue));
const dragging = ref(null); // 'start', 'end', or 'dynamic'
const sliderWidth = ref(0);
const sliderLeft = ref(0);
const showTooltip = ref({
  start: false,
  end: false,
});

// Additional References for Dynamic Dragging
const initialClientX = ref(null);
const dynamicDragHandle = ref(null);

// Computed Property for Range Style
const rangeStyle = computed(() => {
  const left = Math.min(start.value, end.value);
  const width = Math.abs(end.value - start.value);
  return {
    left: `${left}%`,
    width: `${width}%`,
  };
});

// Watchers to update handle positions when props change
watch(
  () => props.startValue,
  (newVal) => {
    start.value = percentageFromValue(newVal);
  }
);

watch(
  () => props.endValue,
  (newVal) => {
    end.value = percentageFromValue(newVal);
  }
);

watch(
  () => props.min,
  () => {
    clampValues();
    updateSliderDimensions();
  }
);

watch(
  () => props.max,
  () => {
    clampValues();
    updateSliderDimensions();
  }
);

// Helper Functions
function valueFromPercentage(percentage) {
  return Math.round((percentage / 100) * (props.max - props.min) + props.min);
}

function percentageFromValue(value) {
  return ((value - props.min) / (props.max - props.min)) * 100;
}

function clampValues() {
  // Ensure startValue and endValue are within min and max
  let newStart = Math.max(props.min, Math.min(props.startValue, props.endValue));
  let newEnd = Math.min(props.max, Math.max(props.endValue, props.startValue));

  if (newStart !== props.startValue) {
    emit('update:start', newStart);
  }
  if (newEnd !== props.endValue) {
    emit('update:end', newEnd);
  }

  start.value = percentageFromValue(newStart);
  end.value = percentageFromValue(newEnd);
}

function getClientX(event) {
  return event.type.startsWith('touch') ? event.touches[0].clientX : event.clientX;
}

function initDrag(handle, event) {
  if (props.disabled) return; // Do not initiate drag if disabled

  event.preventDefault();

  if (props.startValue === props.endValue) {
    dragging.value = 'dynamic';
    initialClientX.value = getClientX(event);
    dynamicDragHandle.value = null;
  } else {
    dragging.value = handle;
  }

  updateSliderDimensions();

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  document.addEventListener('touchmove', onDrag, { passive: false });
  document.addEventListener('touchend', stopDrag);

  // Show tooltip when dragging starts
  if (dragging.value !== 'dynamic') {
    showTooltip.value[handle] = true;
  } else {
    // Show both tooltips initially for dynamic dragging for 1.5 second
    showTooltip.value.start = true;
    showTooltip.value.end = true;
    setTimeout(() => {
      showTooltip.value.start = false;
      showTooltip.value.end = false;
    }, 1500);
  }
}

function onDrag(event) {
  if (!dragging.value || props.disabled) return;

  event.preventDefault();

  let clientX = getClientX(event);
  let percentage = ((clientX - sliderLeft.value) / sliderWidth.value) * 100;
  percentage = Math.min(Math.max(percentage, 0), 100);
  const value = valueFromPercentage(percentage);

  if (dragging.value === 'start') {
    // Ensure start does not exceed end
    const adjustedValue = Math.min(value, props.endValue);
    emit('update:start', adjustedValue);
  } else if (dragging.value === 'end') {
    // Ensure end does not go below start
    const adjustedValue = Math.max(value, props.startValue);
    emit('update:end', adjustedValue);
  } else if (dragging.value === 'dynamic') {
    if (!dynamicDragHandle.value) {
      // Determine direction based on initial and current clientX
      const deltaX = clientX - initialClientX.value;
      if (deltaX < -5) { // Threshold to prevent accidental switch
        dynamicDragHandle.value = 'start';
      } else if (deltaX > 5) {
        dynamicDragHandle.value = 'end';
      }
      // If deltaX is within -5 to 5, do not change handle
    }

    if (dynamicDragHandle.value === 'start') {
      const adjustedValue = Math.min(value, props.endValue);
      emit('update:start', adjustedValue);
    } else if (dynamicDragHandle.value === 'end') {
      const adjustedValue = Math.max(value, props.startValue);
      emit('update:end', adjustedValue);
    }
  }
}

function stopDrag() {
  if (dragging.value === 'dynamic' && dynamicDragHandle.value) {
    showTooltip.value[dynamicDragHandle.value] = false;
  } else if (dragging.value) {
    showTooltip.value[dragging.value] = false;
  }

  dragging.value = null;
  dynamicDragHandle.value = null;
  initialClientX.value = null;

  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.removeEventListener('touchmove', onDrag);
  document.removeEventListener('touchend', stopDrag);
}

function moveHandle(handle, direction) {
  if (props.disabled) return;

  let increment = 1 * direction;
  if (handle === 'start') {
    let newValue = props.startValue + increment;
    newValue = Math.min(Math.max(newValue, props.min), props.endValue);
    emit('update:start', newValue);
  } else if (handle === 'end') {
    let newValue = props.endValue + increment;
    newValue = Math.max(Math.min(newValue, props.max), props.startValue);
    emit('update:end', newValue);
  }

  // Show tooltip briefly
  showTooltip.value[handle] = true;
  setTimeout(() => {
    showTooltip.value[handle] = false;
  }, 1000);
}

function updateSliderDimensions() {
  if (sliderRef.value) {
    const rect = sliderRef.value.getBoundingClientRect();
    sliderWidth.value = rect.width;
    sliderLeft.value = rect.left;
  }
}

// Lifecycle Hooks
onMounted(() => {
  updateSliderDimensions();
  window.addEventListener('resize', updateSliderDimensions);
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', updateSliderDimensions);
  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.removeEventListener('touchmove', onDrag);
  document.removeEventListener('touchend', stopDrag);
});
</script>

<style scoped>
/* Ensure focus outlines are visible */
div[role="slider"]:focus {
  outline: 2px solid #3b82f6; /* Tailwind's blue-500 */
  outline-offset: 2px;
}

/* Tooltip transition */
div[role="slider"] > div {
  transition: opacity 0.3s;
}

/* Disabled state styles */
.bg-gray-300 {
  cursor: not-allowed;
}
</style>
